/**
 * Gerenciador de Autenticação HMAC
 * 
 * Responsável por:
 * - Validar disponibilidade de credenciais HMAC
 * - Injetar assinaturas HMAC nos payloads
 * - Bloquear envios sem autenticação quando obrigatório
 * - Logs detalhados de autenticação
 * 
 * @module AuthManager
 */

import { generateAuth } from '../../hmac';
import { TOKEN_EXEMPT_ACTIONS, HMAC_EXEMPT_ACTIONS } from '../../constants';
import { sendToTerminal } from '../../../terminalLogger';
import { LogCategory, shouldLogAuthDetails, getLogLevel } from '@/config/logging';

export interface AuthResult {
  success: boolean;
  data?: Record<string, any>;
  error?: {
    requestId: string;
    status: string;
    message: string;
    error?: {
      code: string;
      message: string;
    };
  };
}

export interface ValidationResult {
  valid: boolean;
  reason?: string;
  blocked?: boolean;
}

/**
 * Gerenciador de autenticação HMAC para comunicação com CorelDRAW
 */
export class AuthManager {
  /**
   * Verifica se uma action é isenta de sessionToken na raiz da mensagem
   */
  public isTokenExemptAction(action: string): boolean {
    // Verificar lista estática
    if (TOKEN_EXEMPT_ACTIONS.has(action)) {
      return true;
    }
    // Verificar debug:* dinamicamente
    return action.toLowerCase().startsWith('debug:');
  }

  /**
   * Verifica se uma action é isenta de HMAC (data.auth)
   * 
   * Conforme contrato do backend C#:
   * - app-status: isenta de HMAC (mas exige sessionToken)
   * - health:pong: isenta de HMAC (mas exige sessionToken)
   * - session:hello: isenta de HMAC (e de sessionToken)
   * - tools:bootstrap-session: isenta de HMAC (e de sessionToken)
   * - debug:*: isenta de HMAC (e também de sessionToken)
   */
  public isHmacExemptAction(action: string): boolean {
    // Verificar lista estática
    if (HMAC_EXEMPT_ACTIONS.has(action)) {
      return true;
    }
    // Verificar debug:* dinamicamente
    return action.toLowerCase().startsWith('debug:');
  }

  /**
   * Verifica se o HMAC secret está disponível
   */
  public isHmacSecretAvailable(): boolean {
    const hmacSecret = (window as any).sessionCrypto?.hmacKeyB64;
    return !!hmacSecret;
  }

  /**
   * Valida se os requisitos de autenticação estão satisfeitos
   * 
   * ⚠️ IMPORTANTE: Esta validação é apenas para actions que EXIGEM HMAC.
   * Actions isentas de HMAC não passam por esta validação (são tratadas em addAuthToPayload).
   */
  public validateAuthRequirements(action: string, requestId: string): ValidationResult {
    // Actions isentas de HMAC não precisam validar secret (são tratadas separadamente)
    if (this.isHmacExemptAction(action)) {
      return { valid: true };
    }

    // Actions que EXIGEM HMAC: verificar se secret está disponível
    if (!this.isHmacSecretAvailable()) {
      // ✅ DEBUG: Verificar se pode ser erro de launcher não pronto
      const launcherState = (window as any).__LAUNCHER_STATE || 'unknown';
      
      // Erros sempre são logados (categoria ERROR)
      sendToTerminal('🚫 [HMAC:REQUIRED_BUT_UNAVAILABLE]', {
        action,
        requestId,
        reason: 'window.sessionCrypto.hmacKeyB64 not found',
        mode: process.env.NODE_ENV,
        blocked: true,
        launcherState,
        mightBeLauncherBooting: launcherState === 'booting' || action === 'lock-document' || action === 'shortcuts:disable',
        help: 'Execute esta aplicação dentro do CorelDRAW WebView para ter acesso às variáveis de autenticação.'
      }, LogCategory.ERROR);

      return {
        valid: false,
        reason: 'HMAC secret unavailable',
        blocked: true
      };
    }

    return { valid: true };
  }

  /**
   * Extrai campos sensíveis que NÃO devem fazer parte do HMAC
   * 
   * ⚠️ IMPORTANTE: O backend C# canonicaliza o SMB COMPLETO (com username e password).
   * O frontend DEVE fazer o mesmo: incluir smb:{password:"...",username:"..."} na canonicalização.
   * 
   * ÚNICA EXTRAÇÃO: Remover campo 'auth' de retries anteriores para evitar HMAC incorreto.
   * 
   * @param data Dados completos da mensagem
   * @returns { dataForHmac, sensitiveFields } - dados para HMAC e campos sensíveis extraídos
   */
  private extractSensitiveFields(data: Record<string, any>): {
    dataForHmac: Record<string, any>;
    sensitiveFields: Record<string, any>;
  } {
    const dataForHmac = { ...data };
    const sensitiveFields: Record<string, any> = {};

    // ⚠️ CRÍTICO: Remover campo 'auth' se existir
    // Isso previne que um HMAC antigo (de retry anterior) seja incluído
    // na canonicalização do novo HMAC, causando assinatura incorreta.
    // 
    // PROBLEMA: Em retries, o payload pode conter o 'auth' da tentativa anterior.
    // Se não removermos, o canonicalizeBody() incluirá o auth antigo no cálculo,
    // gerando uma assinatura HMAC incorreta.
    //
    // SOLUÇÃO: Sempre remover 'auth' antes de calcular novo HMAC.
    if ('auth' in dataForHmac) {
      // Guardar auth antigo (caso necessário para debug)
      sensitiveFields.oldAuth = dataForHmac.auth;
      
      // REMOVER auth antes de gerar novo HMAC
      delete dataForHmac.auth;
    }

    // ✅ CORREÇÃO: Remover sessionToken também (conforme backend C# MessageHandlerService.cs linhas 990-991)
    // O sessionToken está na raiz da mensagem (message.sessionToken), mas se estiver em data,
    // deve ser removido antes da canonicalização para evitar divergência no HMAC.
    if ('sessionToken' in dataForHmac) {
      delete dataForHmac.sessionToken;
    }

    // ✅ CORREÇÃO: NÃO remover SMB antes do HMAC!
    // O backend C# canonicaliza com SMB completo: smb:{password:"...",username:"..."}
    // O frontend DEVE fazer o mesmo para gerar HMAC correto.

    return { dataForHmac, sensitiveFields };
  }

  /**
   * Re-adiciona campos sensíveis após a geração do HMAC
   * 
   * ⚠️ NOTA: Atualmente apenas o campo 'auth' é extraído (para evitar retries incorretos).
   * O SMB NÃO é mais extraído, pois o backend C# canonicaliza com SMB completo.
   * 
   * @param data Dados autenticados (com auth)
   * @param sensitiveFields Campos sensíveis extraídos anteriormente
   * @returns Dados completos (sem alterações, pois SMB não é mais extraído)
   */
  private readdSensitiveFields(
    data: Record<string, any>,
    sensitiveFields: Record<string, any>
  ): Record<string, any> {
    // ✅ CORREÇÃO: SMB não é mais extraído, então não precisa re-adicionar
    // Apenas retornar os dados como estão (SMB já está presente)
    return data;
  }

  /**
   * Adiciona autenticação HMAC ao payload
   * 
   * Injeta `data.auth = { signature, nonce, timestamp }` conforme especificação webview-rules.md
   * 
   * ⚠️ IMPORTANTE: 
   * - Actions isentas de HMAC (HMAC_EXEMPT_ACTIONS) NÃO recebem data.auth
   * - Actions isentas de sessionToken (TOKEN_EXEMPT_ACTIONS) ainda EXIGEM HMAC (exceto se também forem isentas de HMAC)
   * - O campo 'auth' de retries anteriores é removido antes do HMAC (para evitar HMAC incorreto)
   * - O SMB é mantido COMPLETO na canonicalização (backend C# espera smb:{password:"...",username:"..."})
   * 
   * Conforme contrato do backend C# (05/12/2025):
   * - app-status e health:pong: isentas de HMAC (mas exigem sessionToken)
   * - session:hello e tools:bootstrap-session: isentas de HMAC e de sessionToken
   * - open-tools-docker: isenta de sessionToken, mas exige HMAC
   * - debug:*: isentas de ambos
   * 
   * @param action Nome da action
   * @param data Dados da mensagem
   * @param requestId ID da requisição
   * @returns Resultado com payload autenticado ou erro
   */
  public addAuthToPayload(action: string, data: Record<string, any>, requestId: string): AuthResult {
    // ✅ CORREÇÃO CRÍTICA (03/12/2025): Verificar se action é isenta de HMAC
    // Se for isenta, retornar dados sem auth (conforme contrato do backend C#)
    if (this.isHmacExemptAction(action)) {
      // Remover auth antigo se existir (para limpeza)
      const cleanData = { ...data };
      if ('auth' in cleanData) {
        delete cleanData.auth;
      }
      
      // Log apenas em nível DEBUG
      if (shouldLogAuthDetails()) {
        sendToTerminal('✅ [HMAC:EXEMPT] Action isenta de HMAC', {
          action,
          requestId,
          note: 'Dados enviados sem data.auth conforme contrato do backend C#'
        }, LogCategory.AUTH);
      }
      
      return {
        success: true,
        data: cleanData
      };
    }

    // Actions que EXIGEM HMAC: validar requisitos
    const validation = this.validateAuthRequirements(action, requestId);
    if (!validation.valid) {
      return {
        success: false,
        error: {
          requestId,
          status: 'erro',
          message: 'HMAC obrigatório mas chave secreta não disponível',
          error: {
            code: 'HMAC_SECRET_UNAVAILABLE',
            message: 'window.sessionCrypto.hmacKeyB64 não encontrado. Execute dentro do CorelDRAW WebView.'
          }
        }
      };
    }

    // Obter HMAC secret
    const hmacSecret = (window as any).sessionCrypto?.hmacKeyB64;

    try {
      // ⚠️ CRÍTICO: Extrair campos sensíveis ANTES de gerar HMAC
      const { dataForHmac, sensitiveFields } = this.extractSensitiveFields(data);
      
      // Apenas nível 3 loga detalhes de autenticação
      if (shouldLogAuthDetails()) {
        // DEBUG: Log do data ANTES de gerar HMAC
        const hasIdempotencyKey = 'idempotencyKey' in dataForHmac;
        const idempotencyKeyValue = hasIdempotencyKey ? (dataForHmac as any).idempotencyKey : undefined;
        const hasSensitiveFields = Object.keys(sensitiveFields).length > 0;
        const hadOldAuth = 'oldAuth' in sensitiveFields;
        
        sendToTerminal('🔍 [AUTH:PRE_HMAC]', {
          action,
          requestId,
          dataKeys: Object.keys(dataForHmac).sort(),
          dataIsEmpty: Object.keys(dataForHmac).length === 0,
          dataPreview: JSON.stringify(dataForHmac).substring(0, 200),
          // 🔍 DEBUG: Verificar se auth antigo foi removido
          hadOldAuthInPayload: hadOldAuth,
          oldAuthRemoved: hadOldAuth ? '✅ AUTH ANTIGO REMOVIDO ANTES DE HMAC' : 'sem auth antigo',
          oldAuthPreview: hadOldAuth && sensitiveFields.oldAuth ? {
            hasSignature: !!sensitiveFields.oldAuth.signature,
            hasNonce: !!sensitiveFields.oldAuth.nonce,
            hasTimestamp: !!sensitiveFields.oldAuth.timestamp,
            noncePreview: sensitiveFields.oldAuth.nonce?.substring(0, 8) + '...',
            timestamp: sensitiveFields.oldAuth.timestamp
          } : undefined,
          hasAuthInDataForHmac: 'auth' in dataForHmac,  // ⚠️ DEVE SER FALSE!
          authCleanStatus: ('auth' in dataForHmac) ? '❌ ERRO: auth ainda presente!' : '✅ auth removido corretamente',
          // 🔍 DEBUG: Rastreamento de idempotencyKey
          hasIdempotencyKey,
          idempotencyKeyValue: hasIdempotencyKey ? (typeof idempotencyKeyValue === 'string' ? idempotencyKeyValue.substring(0, 50) + (idempotencyKeyValue.length > 50 ? '...' : '') : String(idempotencyKeyValue)) : undefined,
          idempotencyKeyType: hasIdempotencyKey ? typeof idempotencyKeyValue : undefined,
          idempotencyKeyLocation: hasIdempotencyKey ? 'data.idempotencyKey ✅' : 'NÃO ENCONTRADO ❌',
          note: hasIdempotencyKey ? 'idempotencyKey será incluído na canonicalização' : 'idempotencyKey não está presente',
          // 🔒 DEBUG: Campos sensíveis extraídos
          sensitiveFieldsExtracted: hasSensitiveFields,
          sensitiveFieldsCount: Object.keys(sensitiveFields).length,
          sensitiveFieldsList: Object.keys(sensitiveFields),
          smbStructure: dataForHmac.smb 
            ? (Object.keys(dataForHmac.smb).length === 0 
              ? 'smb:{} (vazio)' 
              : `smb:{${Object.keys(dataForHmac.smb).join(',')}} ✅ COMPLETO (alinhado com C#)`)
            : 'sem smb',
          smbNote: 'SMB incluído na canonicalização (backend C# espera SMB completo)'
        }, LogCategory.AUTH);
      }
      
      // Gerar HMAC usando dados completos (SMB incluído, apenas 'auth' removido)
      // Formato: ${nonce}|${timestamp}|${action}|${canonicalBody}
      // ⚠️ IMPORTANTE: Backend C# canonicaliza com SMB completo, então frontend também deve incluir
      const auth = generateAuth(action, dataForHmac, hmacSecret);

      // Criar cópia dos dados com auth injetado
      const authenticatedData = {
        ...dataForHmac,
        auth: {
          signature: auth.signature,  // Base64
          nonce: auth.nonce,          // UUID v4
          timestamp: auth.timestamp   // Unix seconds
        }
      };

      // ⚠️ NOTA: readdSensitiveFields agora apenas retorna os dados (SMB não é mais extraído)
      const finalData = this.readdSensitiveFields(authenticatedData, sensitiveFields);

      // Apenas nível 3 loga detalhes de autenticação
      if (shouldLogAuthDetails()) {
        const hasSensitiveFields = Object.keys(sensitiveFields).length > 0;
        
        sendToTerminal('✅ [HMAC:INJECTED]', {
          action,
          requestId,
          location: 'data.auth ✅',
          hasSignature: true,
          hasNonce: true,
          hasTimestamp: true,
          signatureFormat: 'Base64 ✅',
          nonceFormat: 'UUID v4 ✅',
          noncePreview: auth.nonce.substring(0, 8) + '...',
          timestamp: auth.timestamp,
          timestampFormat: 'Unix seconds ✅',
          mode: process.env.NODE_ENV,
          required: true,
          spec: 'webview-rules.md CORRETO ✅',
          // 🔒 DEBUG: Campos sensíveis re-adicionados
          sensitiveFieldsReadded: hasSensitiveFields,
          smbFinalStructure: finalData.smb 
            ? (Object.keys(finalData.smb).length === 0 
              ? 'smb:{} (vazio)' 
              : `smb:{${Object.keys(finalData.smb).join(',')}} ✅ COMPLETO`)
            : 'sem smb',
          smbNote: 'SMB mantido completo (não foi extraído antes do HMAC)'
        }, LogCategory.AUTH);
      }

      return {
        success: true,
        data: finalData
      };
    } catch (error) {
      // Erros sempre são logados (categoria ERROR)
      sendToTerminal('❌ [HMAC:GENERATION_ERROR]', {
        action,
        requestId,
        error: error instanceof Error ? error.message : String(error),
        blocked: true
      }, LogCategory.ERROR);

      return {
        success: false,
        error: {
          requestId,
          status: 'erro',
          message: 'Erro ao gerar assinatura HMAC',
          error: {
            code: 'HMAC_GENERATION_FAILED',
            message: error instanceof Error ? error.message : String(error)
          }
        }
      };
    }
  }
}

